import { CacheManager } from '../utils/cacheManager';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';
import { API_ENDPOINTS, buildQueryString, API_BASE_URL, resolveShopBusinessId } from '../config/api';
import { rewriteLegacyShopAssetUrl } from '../utils/rewriteLegacyShopAssetUrl';

const isDev = process.env.NODE_ENV === 'development';

/** First preview/mockup URL from POD printFiles JSON (Printful fulfillment payload). */
const firstImageFromPrintFiles = (printFiles) => {
  if (printFiles == null) return null;
  let arr = printFiles;
  if (typeof printFiles === 'string') {
    try {
      arr = JSON.parse(printFiles);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(arr)) return null;
  for (const entry of arr) {
    if (!entry || typeof entry !== 'object') continue;
    const files = entry.files;
    if (!Array.isArray(files)) continue;
    for (const f of files) {
      if (!f || typeof f !== 'object') continue;
      const url = f.url || f.preview_url || f.thumbnail_url || f.file_url;
      if (typeof url === 'string' && url.length > 0) return url;
    }
  }
  return null;
};

/**
 * Map Mango Business System product to frontend product format
 */
const mapMangoProductToFrontend = (mangoProduct) => {
  if (!mangoProduct || typeof mangoProduct !== 'object') {
    return null;
  }

  // Helper function to construct full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    let resolved;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      resolved = imagePath;
    } else if (imagePath.startsWith('/uploads')) {
      const apiBase = API_BASE_URL.replace('/api/v1', '');
      resolved = `${apiBase}${imagePath}`;
    } else {
      resolved = imagePath;
    }
    return rewriteLegacyShopAssetUrl(resolved);
  };

  // Handle images - Mango stores as JSON array
  let images = [];
  if (mangoProduct.images) {
    if (typeof mangoProduct.images === 'string') {
      try {
        images = JSON.parse(mangoProduct.images);
      } catch (e) {
        images = [];
      }
    } else if (Array.isArray(mangoProduct.images)) {
      images = mangoProduct.images;
    }
  }

  // Convert image paths to full URLs
  images = images.map(img => getImageUrl(img)).filter(img => img !== null);

  const rawVariantRows = Array.isArray(mangoProduct.variants) ? mangoProduct.variants : [];
  const firstVariantImageUrl = rawVariantRows.length
    ? getImageUrl(rawVariantRows.find((v) => v && v.image)?.image)
    : null;

  const printFileImage = firstImageFromPrintFiles(mangoProduct.printFiles);

  // Get primary image or first gallery / variant / Printful file URL
  let primaryImage =
    getImageUrl(mangoProduct.primaryImage) ||
    (images.length > 0 ? images[0] : null) ||
    firstVariantImageUrl ||
    (printFileImage ? getImageUrl(printFileImage) : null);

  if (images.length === 0 && primaryImage) {
    images = [primaryImage];
  }

  // Map gallery images format (for compatibility with existing frontend)
  const galleryImages = images.map((url, index) => ({
    url: url,
    index: index,
    isPrimary: index === 0 || url === primaryImage
  }));

  // Map variants if they exist
  const variants = rawVariantRows.length > 0
    ? rawVariantRows.map(v => {
        const options = {
          option1: v.option1Name && v.option1Value ? { name: v.option1Name, value: v.option1Value } : null,
          option2: v.option2Name && v.option2Value ? { name: v.option2Name, value: v.option2Value } : null,
          option3: v.option3Name && v.option3Value ? { name: v.option3Name, value: v.option3Value } : null,
        };

        // Extract size and color by scanning all options for known names
        const allOpts = [options.option1, options.option2, options.option3].filter(Boolean);
        const sizeOpt = allOpts.find(o => o.name?.toLowerCase() === 'size');
        const colorOpt = allOpts.find(o => o.name?.toLowerCase() === 'color');

        return {
          id: v.id,
          name: v.name,
          price: v.price || mangoProduct.price,
          compareAtPrice: v.compareAtPrice || mangoProduct.compareAtPrice,
          inventoryQty: v.inventoryQty || 0,
          isActive: v.isActive !== false,
          options,
          size: sizeOpt?.value || null,
          color: colorOpt?.value || null,
          image: v.image || primaryImage,
        };
      })
    : [];

  const printfulId = mangoProduct.printfulProductId || mangoProduct.printfulSyncId;

  // Build frontend product object
  const frontendProduct = {
    // Basic Info
    id: mangoProduct.id,
    name: mangoProduct.name,
    description: mangoProduct.description || '',
    shortDescription: mangoProduct.shortDescription || mangoProduct.description || '',
    
    // Pricing
    price: parseFloat(mangoProduct.price) || 0,
    compareAtPrice: mangoProduct.compareAtPrice ? parseFloat(mangoProduct.compareAtPrice) : null,
    salePrice: mangoProduct.salePrice != null ? parseFloat(mangoProduct.salePrice) : null,
    originalPrice: mangoProduct.originalPrice != null ? parseFloat(mangoProduct.originalPrice) : null,
    isOnSale: !!mangoProduct.isOnSale,
    saleInfo: mangoProduct.saleInfo || null,
    variantSalePrices: mangoProduct.variantSalePrices || null,
    
    // Product Type
    productType: mangoProduct.productType || 'physical',
    type: mangoProduct.productType || 'physical',
    category: mangoProduct.category || '',
    
    // Images
    images: images,
    primaryImage: primaryImage,
    customImage: primaryImage,
    galleryImages: galleryImages,
    folder: null, // Not used in Mango system, but kept for compatibility
    
    // Variants
    variants: variants,
    hasVariants: mangoProduct.hasVariants || (variants.length > 0),
    
    // Subscription Info
    subscription: mangoProduct.productType === 'subscription',
    subscriptionPrice: mangoProduct.subscriptionBasePrice ? parseFloat(mangoProduct.subscriptionBasePrice) : null,
    subscriptionInterval: mangoProduct.subscriptionBillingCycle || null,

    // Pricing Config (donation settings, gift card amounts, etc.)
    pricingConfig: mangoProduct.pricingConfig || null,

    // Digital / donation attachment (public API: hasDigitalAttachment only; no raw file URL)
    hasDigitalAttachment: !!mangoProduct.hasDigitalAttachment,

    donatedTotal:
      mangoProduct.donatedTotal != null ? Number(mangoProduct.donatedTotal) : undefined,
    
    // Inventory
    trackInventory: mangoProduct.trackInventory !== false,
    inventoryQty: mangoProduct.inventoryQty || 0,
    lowStockThreshold: mangoProduct.lowStockThreshold || 5,
    
    // Service Product Settings (for coaching, consulting, etc.)
    maxClients: mangoProduct.maxClients || null,
    remainingSpots: mangoProduct.remainingSpots !== undefined ? mangoProduct.remainingSpots : null,
    
    // Status
    published: mangoProduct.isActive !== false,
    isActive: mangoProduct.isActive !== false,
    isFeatured: mangoProduct.isFeatured || false,
    
    // SEO
    slug: mangoProduct.slug || null,
    seoTitle: mangoProduct.seoTitle || null,
    seoDescription: mangoProduct.seoDescription || null,
    
    // Tags and Collections
    tags: mangoProduct.tags ? (typeof mangoProduct.tags === 'string' ? JSON.parse(mangoProduct.tags) : mangoProduct.tags) : [],
    collections: mangoProduct.collections ? (typeof mangoProduct.collections === 'string' ? JSON.parse(mangoProduct.collections) : mangoProduct.collections) : [],
    
    // Metadata
    createdAt: mangoProduct.createdAt,
    updatedAt: mangoProduct.updatedAt,

    // POD: Product detail page fallbacks read printfulData.designThumbnailUrl
    printfulData: printfulId
      ? {
          printfulProductId: String(printfulId),
          designThumbnailUrl: printFileImage || primaryImage || (images[0] ?? null),
        }
      : mangoProduct.printfulData ?? null,

    // Printful size guide data (available_sizes + size_tables with measurements)
    sizeGuide: mangoProduct.sizeGuide || null,
  };

  if (!primaryImage && images.length === 0) {
    console.warn('[PRODUCT-CREATE-FLOW]', 'shop.display.no-image-after-map', {
      id: mangoProduct.id,
      name: mangoProduct.name,
      rawPrimary: mangoProduct.primaryImage ?? null,
      rawImagesType: Array.isArray(mangoProduct.images) ? 'array' : typeof mangoProduct.images,
    });
  }

  return frontendProduct;
};

class ProductsService {
  constructor() {
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes cache
    if (isDev) {
      console.log('🚀 [SERVICE] ProductsService initialized');
      console.log('🌐 [SERVICE] Using Mango Business System API');
      console.log('🌐 [SERVICE] API Base:', API_ENDPOINTS.products.public);
    }
  }

  async getProducts(options = {}) {
    if (isDev) {
      console.log('🚀 [FUNCTION] getProducts called');
      console.log('🚀 [START] Fetching products from Mango API...');
    }

    // Check cache first (unless forceRefresh is true)
    if (!options.forceRefresh) {
      const cached = this.cacheManager.get();
      if (cached) {
        if (isDev) console.log('💾 [CACHE] Returning cached products:', cached.length);
        return cached;
      }
    } else {
      this.clearCache();
      if (isDev) console.log('🗑️ [CACHE] Cache cleared - forcing fresh fetch');
    }

    try {
      const tenantId = await resolveShopBusinessId();
      if (isDev && tenantId) {
        console.log('🌐 [SERVICE] Tenant (business) ID:', tenantId);
      }
      // Build query parameters
      const queryParams = {
        businessId: tenantId,
        isActive: 'true',
        limit: options.limit || 100, // Get more products by default
        page: options.page || 1,
      };

      // Add optional filters
      if (options.category) queryParams.category = options.category;
      if (options.productType) queryParams.productType = options.productType;
      if (options.search) queryParams.search = options.search;
      if (options.isFeatured !== undefined) queryParams.isFeatured = options.isFeatured;

      const queryString = buildQueryString(queryParams);

      // Add dynamic attribute filters: { attr: { burners: 4, fuel_type: 'gas' } }
      // These are appended as ?attr[burners]=4&attr[fuel_type]=gas
      let attrString = '';
      if (options.attr && typeof options.attr === 'object') {
        for (const [key, value] of Object.entries(options.attr)) {
          if (value !== undefined && value !== null && value !== '' && 
              !(Array.isArray(value) && value.length === 0)) {
            // Multi-select: repeat key for each value
            if (Array.isArray(value)) {
              value.forEach(v => { attrString += `&attr[${encodeURIComponent(key)}]=${encodeURIComponent(v)}`; });
            } else if (typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
              // Range filter — sent as min/max separate keys (backend handles range via path query)
              if (value.min !== undefined) attrString += `&attr_min[${encodeURIComponent(key)}]=${encodeURIComponent(value.min)}`;
              if (value.max !== undefined) attrString += `&attr_max[${encodeURIComponent(key)}]=${encodeURIComponent(value.max)}`;
            } else {
              attrString += `&attr[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`;
            }
          }
        }
      }

      const url = `${API_ENDPOINTS.products.public}?${queryString}${attrString}`;

      const response = await fetch(url);
      if (isDev) {
        console.log('📡 [API] Fetching products from Mango API:', url);
        console.log('📡 [API] Response received:', { status: response.status, ok: response.ok });
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      // Map Mango products to frontend format
      const mangoProducts = data.products || [];
      const frontendProducts = mangoProducts
        .map(mapMangoProductToFrontend)
        .filter(Boolean);

      // Cache the mapped products
      this.cacheManager.set(frontendProducts);
      // Also save to localStorage as fallback
      this.saveProductsToLocalStorage(frontendProducts);
      if (isDev) console.log('✅ [SUCCESS] Products loaded from Mango API:', frontendProducts.length);
      return frontendProducts;
    } catch (error) {
      console.error('❌ [ERROR] Error fetching products from Mango API:', error);
      console.error('❌ [ERROR] Error details:', {
        message: error.message,
        stack: error.stack,
        url: API_ENDPOINTS.products.public
      });
      
      // Fallback to localStorage if API fetch fails
      const fallbackProducts = this.getProductsFromLocalStorage();
      if (fallbackProducts.length > 0) {
        if (isDev) {
          console.warn('⚠️ API fetch failed, using localStorage fallback. This may be stale data.');
          console.log('📦 [PRODUCTS-SERVICE] Fallback products from localStorage:', fallbackProducts.length);
        }
        // Also update the cache with fallback data
        this.cacheManager.set(fallbackProducts);
        return fallbackProducts;
      }
      
      // Return empty array if no fallback available
      console.error('❌ No products available from API or localStorage');
      return [];
    }
  }

  async getProduct(productId) {
    if (isDev) console.log('🚀 [FUNCTION] getProduct called with ID:', productId);

    try {
      const url = API_ENDPOINTS.products.publicById(productId);
      const response = await fetch(url);
      if (isDev) console.log('📡 [API] Fetching product from Mango API:', url);
      
      if (!response.ok) {
        if (response.status === 404) {
          if (isDev) console.log('⚠️ [WARN] Product not found:', productId);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const mangoProduct = data.product || data; // Handle both formats

      // Map to frontend format
      const frontendProduct = mapMangoProductToFrontend(mangoProduct);
      if (isDev) console.log('✅ [SUCCESS] Product loaded:', productId);
      return frontendProduct || null;
    } catch (error) {
      console.error('❌ [ERROR] Error fetching product from Mango API:', error);
      console.error('❌ [ERROR] Error details:', {
        productId,
        message: error.message,
        stack: error.stack,
        url: API_ENDPOINTS.products.publicById(productId)
      });
      return null;
    }
  }

  clearCache() {
    this.cacheManager.clear();
    if (isDev) console.log('🗑️ [CACHE] Products cache cleared');
  }

  /**
   * Get products from localStorage (fallback)
   */
  getProductsFromLocalStorage() {
    const products = safeStorage.getJSON(STORAGE_KEYS.PRODUCTS || 'rainbow_sails_products', []);
    if (isDev && products.length > 0) {
      console.log('💾 [PRODUCTS-SERVICE] Retrieved from localStorage:', products.length);
    }
    return products;
  }

  /**
   * Save products to localStorage (for offline access)
   */
  saveProductsToLocalStorage(products) {
    if (safeStorage.setJSON(STORAGE_KEYS.PRODUCTS || 'rainbow_sails_products', products) && isDev) {
      console.log('💾 Products saved to localStorage:', products.length);
    }
  }
}

const productsServiceInstance = new ProductsService();
export default productsServiceInstance;